import type { Store as BlockSuiteStore } from '@blocksuite/affine/store';
import { LiveData, Store } from '@toeverything/infra';
import { combineLatest, map } from 'rxjs';

import type { WorkspaceServerService } from '../../cloud';
import type { DocsService } from '../../doc';
import type { WorkspaceService } from '../../workspace';
import { extractTasksFromDoc } from '../extract-tasks';
import type { ExtractedTask } from '../extract-tasks';
import type { TaskItem } from '../types';

const SCAN_IDLE_TIMEOUT = 2000;
const SCAN_PER_SLICE = 8;
const DOC_LOAD_TIMEOUT = 15000;
const DOC_UPDATE_DEBOUNCE = 200;

function scheduleIdle(callback: () => void): () => void {
  if (typeof requestIdleCallback === 'function') {
    const handle = requestIdleCallback(callback, {
      timeout: SCAN_IDLE_TIMEOUT,
    });
    return () => cancelIdleCallback(handle);
  }
  const handle = window.setTimeout(callback, 200);
  return () => window.clearTimeout(handle);
}

function tasksEqual(a: TaskItem[] | undefined, b: TaskItem[] | undefined) {
  if (a === b) return true;
  if (!a || !b || a.length !== b.length) return false;
  return a.every((item, i) => {
    const other = b[i];
    return (
      item.id === other.id &&
      item.title === other.title &&
      item.status === other.status &&
      item.statusLabel === other.statusLabel &&
      item.done === other.done &&
      item.due === other.due
    );
  });
}

/**
 * Scans workspace docs for blocks that mention the current user and exposes
 * the resulting tasks as a live map keyed by doc id.
 *
 * The indexer does not store mention info, so candidate detection runs
 * directly on the Yjs models: docs are scanned in idle callbacks (a few docs
 * per slice) and re-scanned only when the doc's `updatedDate`/title changed
 * or when the doc content itself changes (live updates).
 */
export class MyTasksStore extends Store {
  /** tasks grouped by doc id */
  readonly tasksMap$ = new LiveData<Map<string, TaskItem[]>>(new Map());
  /** true while there are docs waiting to be scanned */
  readonly scanning$ = new LiveData(false);

  private readonly cache = new Map<string, string>();
  private readonly docUnsubscribes = new Map<string, () => void>();
  private readonly pending = new Set<string>();
  private readonly docUpdateTimers = new Map<string, number>();
  private fingerprints: Map<string, DocFingerprint> = new Map();
  private userId: string | null = null;
  private cancelIdle: (() => void) | null = null;

  constructor(
    private readonly docsService: DocsService,
    private readonly workspaceService: WorkspaceService,
    private readonly workspaceServerService: WorkspaceServerService
  ) {
    super();

    const userIdSubscription = LiveData.computed(get => {
      const server = get(this.workspaceServerService.server$);
      const account = server ? get(server.account$) : null;
      return account?.id ?? null;
    }).subscribe(userId => {
      if (userId === this.userId) return;
      this.userId = userId;
      // mention matching depends on the user id — invalidate everything
      this.cache.clear();
      this.rescheduleAll();
    });
    this.disposables.push(() => userIdSubscription.unsubscribe());

    const fingerprintsSubscription = combineLatest([
      this.docsService.allDocsUpdatedDate$(),
      this.docsService.allDocTitle$(),
      this.docsService.allNonTrashDocIds$(),
    ])
      .pipe(
        map(([updatedDates, titles, nonTrashIds]) => {
          const nonTrash = new Set(nonTrashIds);
          const titleById = new Map(titles.map(t => [t.id, t.title]));
          const fingerprints = new Map<string, DocFingerprint>();
          for (const { id, updatedDate } of updatedDates) {
            if (!nonTrash.has(id)) continue;
            fingerprints.set(id, {
              updatedAt: updatedDate,
              title: titleById.get(id) ?? '',
            });
          }
          return fingerprints;
        })
      )
      .subscribe(fingerprints => {
        this.fingerprints = fingerprints;
        this.rescheduleAll();
      });
    this.disposables.push(() => fingerprintsSubscription.unsubscribe());

    this.disposables.push(() => {
      this.cancelIdle?.();
      this.cancelIdle = null;
      for (const unsubscribe of this.docUnsubscribes.values()) {
        unsubscribe();
      }
      this.docUnsubscribes.clear();
      for (const timer of this.docUpdateTimers.values()) {
        window.clearTimeout(timer);
      }
      this.docUpdateTimers.clear();
    });
  }

  private rescheduleAll() {
    for (const [docId, fingerprint] of this.fingerprints) {
      const key = `${this.userId}|${fingerprint.updatedAt ?? ''}|${fingerprint.title}`;
      if (this.cache.get(docId) !== key) {
        this.pending.add(docId);
      }
    }
    // docs removed from the workspace (or moved to trash)
    for (const docId of Array.from(this.cache.keys())) {
      if (!this.fingerprints.has(docId)) {
        this.cache.delete(docId);
        this.setDocTasks(docId, []);
        this.unsubscribeDoc(docId);
      }
    }
    this.scanning$.next(this.pending.size > 0);
    this.pump();
  }

  private pump() {
    if (this.pending.size === 0 || this.cancelIdle) return;
    this.cancelIdle = scheduleIdle(() => {
      this.cancelIdle = null;
      let scanned = 0;
      while (this.pending.size > 0 && scanned < SCAN_PER_SLICE) {
        const docId = this.pending.values().next().value as string;
        this.pending.delete(docId);
        scanned++;
        void this.scanDoc(docId);
      }
      if (this.pending.size > 0) {
        this.pump();
      } else {
        this.scanning$.next(false);
      }
    });
  }

  private async scanDoc(docId: string) {
    const userId = this.userId;
    if (!userId || !this.fingerprints.has(docId)) return;

    try {
      const collection = this.workspaceService.workspace.docCollection;
      const store = collection.getDoc(docId)?.getStore({ id: docId }) ?? null;
      if (!store) {
        this.cache.set(docId, this.fingerprintKey(docId));
        this.setDocTasks(docId, []);
        return;
      }
      try {
        store.load();
      } catch {
        // corrupted doc — treat as no tasks
      }

      this.subscribeDoc(docId, store);

      try {
        await Promise.race([
          this.workspaceService.workspace.engine.doc.waitForDocLoaded(docId),
          new Promise((_, reject) =>
            window.setTimeout(
              () => reject(new Error('doc load timeout')),
              DOC_LOAD_TIMEOUT
            )
          ),
        ]);
      } catch {
        // scan whatever is already loaded
      }

      // doc could be removed while waiting
      if (!this.fingerprints.has(docId)) return;

      const extracted = extractTasksFromDoc(store, docId, userId);
      const tasks = this.toTaskItems(docId, extracted);
      this.cache.set(docId, this.fingerprintKey(docId));
      this.setDocTasks(docId, tasks);
    } catch (e) {
      console.error('failed to scan doc for tasks', docId, e);
      this.cache.set(docId, this.fingerprintKey(docId));
      this.setDocTasks(docId, []);
    }
  }

  private fingerprintKey(docId: string) {
    const fingerprint = this.fingerprints.get(docId);
    return `${this.userId}|${fingerprint?.updatedAt ?? ''}|${fingerprint?.title ?? ''}`;
  }

  private subscribeDoc(docId: string, store: BlockSuiteStore) {
    if (this.docUnsubscribes.has(docId)) return;
    const onDocUpdate = () => {
      const existing = this.docUpdateTimers.get(docId);
      if (existing !== undefined) window.clearTimeout(existing);
      const timer = window.setTimeout(() => {
        this.docUpdateTimers.delete(docId);
        void this.rescanDoc(docId);
      }, DOC_UPDATE_DEBOUNCE);
      this.docUpdateTimers.set(docId, timer);
    };
    store.spaceDoc.on('update', onDocUpdate);
    this.docUnsubscribes.set(docId, () => {
      store.spaceDoc.off('update', onDocUpdate);
    });
  }

  /** re-extract without fingerprint checks (used for live updates) */
  private async rescanDoc(docId: string) {
    const userId = this.userId;
    if (!userId) return;
    const collection = this.workspaceService.workspace.docCollection;
    const store = collection.getDoc(docId)?.getStore({ id: docId }) ?? null;
    if (!store) {
      this.setDocTasks(docId, []);
      return;
    }
    const extracted = extractTasksFromDoc(store, docId, userId);
    const tasks = this.toTaskItems(docId, extracted);
    this.cache.set(docId, this.fingerprintKey(docId));
    this.setDocTasks(docId, tasks);
  }

  private toTaskItems(docId: string, extracted: ExtractedTask[]): TaskItem[] {
    const updatedAt = this.fingerprints.get(docId)?.updatedAt ?? null;
    const docTitle = this.fingerprints.get(docId)?.title ?? '';
    return extracted.map(task => ({ ...task, docTitle, updatedAt }));
  }

  private unsubscribeDoc(docId: string) {
    const unsubscribe = this.docUnsubscribes.get(docId);
    if (unsubscribe) {
      unsubscribe();
      this.docUnsubscribes.delete(docId);
    }
    const timer = this.docUpdateTimers.get(docId);
    if (timer !== undefined) {
      window.clearTimeout(timer);
      this.docUpdateTimers.delete(docId);
    }
  }

  private setDocTasks(docId: string, tasks: TaskItem[]) {
    const current = this.tasksMap$.value;
    const before = current.get(docId);
    if (tasksEqual(before, tasks.length > 0 ? tasks : undefined)) return;

    const next = new Map(current);
    if (tasks.length === 0) {
      next.delete(docId);
    } else {
      next.set(docId, tasks);
    }
    this.tasksMap$.next(next);
  }
}

type DocFingerprint = {
  updatedAt: number | undefined;
  title: string;
};
